<template>
  <section
    class="overview card mt-3"
    :aria-label="t('overview.title')"
    :aria-busy="busy"
  >
    <header class="overview-heading">
      <div>
        <h1>{{ t("overview.title") }}</h1>
        <p>{{ t("overview.subtitle") }}</p>
        <div class="updated">
          {{ t("overview.updated") }} <strong>{{ updatedAt || "—" }}</strong>
        </div>
      </div>
      <div class="scope">
        <span class="scope-label">{{ t("overview.scope") }}</span
        ><i class="material-icons scope-icon" aria-hidden="true">storefront</i>
        <div class="scope-control"><slot name="scope" /></div>
      </div>
    </header>
    <slot name="notice" />
    <div v-if="!selected" class="status" role="status">
      {{ t("overview.chooseShop") }}
    </div>
    <template v-else>
      <div v-if="error" class="status error" role="alert">
        {{ t("overview.loadError") }}
        <MaterialButton
          color="primary"
          :disabled="busy"
          @click="emit('retry')"
          >{{ t("overview.retry") }}</MaterialButton
        >
      </div>
      <div v-else-if="busy || !ready" class="status" role="status">
        {{ t("overview.loading") }}
      </div>
      <section v-if="ready && !busy && !error" class="snapshot-section">
        <h2>{{ t("overview.snapshot") }}</h2>
        <div class="snapshot">
          <div v-for="stat in snapshot" :key="stat.key" class="snapshot-item">
            <span class="snapshot-label">
              <i class="material-icons" aria-hidden="true">{{ stat.icon }}</i>
              <button
                v-if="stat.key === 'defer' && stat.value > 0"
                type="button"
                class="snapshot-label-link"
                @click="emit('report', 'defer')"
              >
                {{ t("overview." + stat.key) }}
              </button>
              <span v-else>{{ t("overview." + stat.key) }}</span>
            </span>
            <div
              class="snapshot-value"
              :class="{ 'line-value': stat.key === 'line' }"
            >
              {{
                stat.key === "line" && (lineLoading || lineFailed)
                  ? "—"
                  : fmt(stat.value)
              }}
              <small>{{
                t("overview." + (stat.key === "line" ? "accounts" : "tickets"))
              }}</small>
            </div>
          </div>
        </div>
      </section>
      <section class="period-section">
        <header class="period-heading">
          <div>
            <h2>{{ t("overview.period") }}</h2>
            <p>{{ t("overview.periodHint") }}</p>
          </div>
          <div class="period-controls">
            <div
              class="segments"
              role="group"
              :aria-label="t('overview.period')"
            >
              <button
                v-for="p in ['today', 'week', 'month']"
                :key="p"
                :aria-pressed="activePeriod === p"
                :disabled="busy"
                @click="choosePeriod(p)"
              >
                {{ t("overview." + p) }}
              </button>
            </div>
            <form class="date-range" @submit.prevent="applyRange">
              <div class="date-field">
                <label for="dp-input-overview-from">{{
                  t("overview.from")
                }}</label
                ><VueDatePicker
                  v-model="draftFrom"
                  uid="overview-from"
                  :format="'dd/MM/yyyy'"
                  auto-apply
                  :teleport="true"
                  :min-date="minDate"
                  :max-date="today"
                  :enable-time-picker="false"
                  :clearable="false"
                  :disabled="busy"
                />
              </div>
              <div class="date-field">
                <label for="dp-input-overview-to">{{ t("overview.to") }}</label
                ><VueDatePicker
                  v-model="draftTo"
                  uid="overview-to"
                  :format="'dd/MM/yyyy'"
                  auto-apply
                  :teleport="true"
                  :min-date="draftFrom"
                  :max-date="today"
                  :enable-time-picker="false"
                  :clearable="false"
                  :disabled="busy"
                />
              </div>
              <MaterialButton type="submit" color="primary" :disabled="busy">{{
                t("overview.apply")
              }}</MaterialButton>
              <p v-if="dateError" class="date-error" role="alert">
                {{ t("overview." + dateError) }}
              </p>
            </form>
          </div>
        </header>
        <template v-if="ready && !busy && !error">
          <div class="kpis">
            <article
              v-for="card in cards"
              :key="card.key"
              class="kpi"
              :class="card.key"
              role="button"
              tabindex="0"
              :aria-label="t('overview.openDetails') + ': ' + t('overview.' + card.key)"
              @click="openCardDetail(card.key)"
              @keydown.enter="openCardDetail(card.key)"
              @keydown.space.prevent="openCardDetail(card.key)"
            >
              <div class="kpi-head">
                <h3>{{ t("overview." + card.key) }}</h3>
                <button
                  class="info-button"
                  :aria-label="
                    t('overview.info') + ': ' + t('overview.' + card.key)
                  "
                  @click.stop="openCardDetail(card.key)"
                >
                  <i class="material-icons" aria-hidden="true">info_outline</i>
                </button>
              </div>
              <div
                class="kpi-value"
                :class="card.key === 'ticketNet' ? valueTone(card.value) : ''"
              >
                <strong>{{
                  card.key === "interest"
                    ? fmt(card.value)
                    : money(card.value / 1000000)
                }}</strong
                ><span>{{
                  t(
                    "overview." +
                      (card.key === "interest" ? "baht" : "million"),
                  )
                }}</span>
              </div>
              <div
                class="kpi-exact"
                :class="card.key === 'ticketNet' ? valueTone(card.value) : ''"
              >
                {{
                  card.key === "interest"
                    ? t("overview.normalInterest")
                    : money(card.value) + " " + t("overview.baht")
                }}
              </div>
              <div class="kpi-footer">
                <span v-if="card.key === 'incoming'"
                  >{{ t("overview.newTickets") }}
                  <b>{{ fmt(model.incomingCount) }}</b>
                  {{ t("overview.tickets") }}</span
                ><span v-else-if="card.key === 'outgoing'"
                  >{{ t("overview.closedTickets") }}
                  <b>{{ fmt(model.outgoingCount) }}</b>
                  {{ t("overview.tickets") }}</span
                ><span v-else-if="card.key === 'interest'">{{
                  t("overview.interestBelow")
                }}</span
                ><span v-else
                  >{{ t("overview.stockNet") }}
                  <b :class="valueTone(model.stockNet)">{{ signed(model.stockNet) }}</b>
                  {{ t("overview.tickets") }}</span
                >
              </div>
            </article>
          </div>
          <section class="panel cashflow" :aria-label="t('overview.cashflow')">
            <header>
              <h2>{{ t("overview.cashflow") }}</h2>
              <p>{{ t("overview.cashHint") }}</p>
            </header>
            <div class="cash-totals">
              <div
                v-for="entry in cashEntries"
                :key="entry.key"
                :class="entry.key"
              >
                <span>{{ t("overview." + entry.key) }}</span>
                <div>
                  <strong>{{
                    entry.key === "cashNet"
                      ? signed(entry.value)
                      : money(entry.value)
                  }}</strong>
                  <small>{{ t("overview.baht") }}</small>
                </div>
                <small v-if="entry.key === 'cashNet'">{{
                  t("overview.cashFormula")
                }}</small>
              </div>
            </div>
            <div
              v-if="model.receive || model.pay"
              class="cash-chart"
              role="img"
              :aria-label="cashDescription"
            >
              <div class="chart-caption">
                {{ t("overview.compareCash") }}
                <span>{{ t("overview.cashUnit") }}</span>
              </div>
              <div
                v-for="entry in cashEntries.slice(0, 2)"
                :key="entry.key"
                class="cash-row"
              >
                <span>{{ t("overview." + entry.key) }}</span>
                <div class="cash-track">
                  <div
                    :class="entry.key"
                    :style="{ width: (entry.value / cashMax) * 100 + '%' }"
                  />
                </div>
                <strong>{{ money(entry.value) }}</strong>
              </div>
            </div>
            <p v-else class="empty">{{ t("overview.zeroCash") }}</p>
            <p class="cash-note">{{ t("overview.cashNote") }}</p>
          </section>
          <div class="charts">
            <section class="panel">
              <header class="panel-heading">
                <div>
                  <h2>{{ t("overview.transactions") }}</h2>
                  <p>{{ t("overview.transactionHint") }}</p>
                </div>
                <div class="segments">
                  <button
                    v-for="mode in ['count', 'value']"
                    :key="mode"
                    :aria-pressed="chartMode === mode"
                    @click="chartMode = mode"
                  >
                    {{
                      t(
                        "overview." +
                          (mode === "count" ? "count" : "ticketValue"),
                      )
                    }}
                  </button>
                </div>
              </header>
              <div class="chart-total">
                {{
                  chartMode === "count" ? fmt(model.count) : money(model.value)
                }}
                <small>{{
                  t("overview." + (chartMode === "count" ? "items" : "baht"))
                }}</small>
              </div>
              <div v-if="chartTotal" class="transaction-bars">
                <button
                  v-for="row in model.rows"
                  :key="row.key"
                  class="transaction-row"
                  :disabled="row.count === 0"
                  @click="openTransactionDetail(row)"
                >
                  <span>{{ rowLabel(row) }}</span
                  ><span class="bar-track"
                    ><span
                      :style="{
                        width: (row[chartMode] / chartMax) * 100 + '%',
                        background: row.color,
                      }" /></span
                  ><span class="bar-value"
                    >{{
                      chartMode === "count" ? fmt(row.count) : money(row.value)
                    }}
                    <small>{{ pct(row[chartMode], chartTotal) }}</small></span
                  >
                </button>
              </div>
              <p v-else class="empty">{{ t("overview.zero") }}</p>
              <p class="panel-note">{{ t("overview.drillHint") }}</p>
            </section>
            <section class="panel">
              <header>
                <h2>{{ t("overview.interestSources") }}</h2>
                <p>{{ t("overview.interestHint") }}</p>
              </header>
              <div class="chart-total">
                {{ money(model.interest) }}
                <small>{{ t("overview.baht") }}</small>
              </div>
              <div
                v-if="model.interest"
                class="interest-stack"
                aria-hidden="true"
              >
                <span
                  v-for="row in model.rows.slice(1)"
                  :key="row.key"
                  :style="{
                    width: (row.interest / model.interest) * 100 + '%',
                    background: row.color,
                  }"
                />
              </div>
              <div class="interest-list">
                <div v-for="row in model.rows.slice(1)" :key="row.key">
                  <span
                    ><i class="dot" :style="{ background: row.color }" />{{
                      rowLabel(row)
                    }}</span
                  ><strong>{{ money(row.interest) }}</strong
                  ><small>{{ pct(row.interest, model.interest) }}</small>
                </div>
              </div>
              <p v-if="!model.interest" class="empty">
                {{ t("overview.zero") }}
              </p>
            </section>
          </div>
          <section class="health">
            <div>
              <h2>{{ t("overview.usage") }}</h2>
              <small>{{ periodLabel }}</small>
            </div>
            <div v-for="stat in usage" :key="stat.key">
              <span>{{ t("overview." + stat.key) }}</span
              ><strong>{{ fmt(stat.value) }}</strong>
            </div>
            <div>
              <span
                >{{ t("overview.otp") }}
                <small>{{ t("overview.otpKinds") }}</small></span
              >
              <div class="otp-values">
                <button :disabled="!n('numOfOtp')" @click="emit('otp', 18)">
                  {{ fmt(n("numOfOtp")) }}
                </button>
                /
                <button
                  :disabled="!n('numOfOtpMigration')"
                  @click="emit('otp', 19)"
                >
                  {{ fmt(n("numOfOtpMigration")) }}
                </button>
              </div>
            </div>
          </section>
          <section class="panel transaction-table">
            <header class="panel-heading">
              <button
                type="button"
                class="transaction-details-toggle"
                :aria-expanded="showTransactionDetails"
                :aria-controls="'overview-transaction-details'"
                :aria-label="
                  t(
                    'overview.' +
                      (showTransactionDetails ? 'collapseDetails' : 'expandDetails'),
                  )
                "
                @click="showTransactionDetails = !showTransactionDetails"
              >
                <span>
                  <strong>{{ t("overview.details") }}</strong>
                  <small>{{ t("overview.detailsHint") }}</small>
                </span>
                <i class="material-icons" aria-hidden="true">
                  {{ showTransactionDetails ? "expand_less" : "expand_more" }}
                </i>
              </button>
              <MaterialCheckbox
                v-if="showTransactionDetails"
                id="overview-migration"
                v-model="showMigration"
                :checked="showMigration"
                name="overview-migration"
                >{{ t("overview.showMigration") }}</MaterialCheckbox
              >
            </header>
            <div
              v-if="showTransactionDetails"
              id="overview-transaction-details"
              class="table-responsive"
            >
              <table>
                <caption class="visually-hidden">
                  {{
                    t("overview.details")
                  }}
                </caption>
                <thead>
                  <tr>
                    <th>{{ t("overview.type") }}</th>
                    <th>{{ t("overview.count") }}</th>
                    <th>
                      {{ t("overview.ticketValue") }} ({{ t("overview.baht") }})
                    </th>
                    <th>
                      {{ t("overview.interestColumn") }} ({{
                        t("overview.baht")
                      }})
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in visibleRows" :key="row.key">
                    <td>
                      <button
                        :disabled="!row.count"
                        @click="openTransactionDetail(row)"
                      >
                        <i class="dot" :style="{ background: row.color }" />{{
                          rowLabel(row)
                        }}<small v-if="row.migration">
                          · {{ t("overview.migration") }}</small
                        >
                      </button>
                    </td>
                    <td>{{ fmt(row.count) }}</td>
                    <td>{{ money(row.value) }}</td>
                    <td
                      :title="
                        row.migration ? t('overview.missingInterest') : ''
                      "
                    >
                      {{ row.interest === null ? "—" : money(row.interest) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <th>
                      {{
                        t(
                          "overview." +
                            (showMigration ? "tableTotal" : "normal"),
                        )
                      }}
                    </th>
                    <td>
                      {{ fmt(visibleRows.reduce((s, r) => s + r.count, 0)) }}
                    </td>
                    <td>
                      {{ money(visibleRows.reduce((s, r) => s + r.value, 0)) }}
                    </td>
                    <td>{{ showMigration ? "—" : money(model.interest) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p v-if="showTransactionDetails && showMigration" class="panel-note">
              {{ t("overview.missingInterest") }}
            </p>
          </section>
          <section class="panel legacy">
            <header class="legacy-heading">
              <h2>{{ t("overview.legacy") }}</h2>
              <MaterialCheckbox
                id="overview-graph"
                v-model="showGraph"
                :checked="showGraph"
                name="overview-graph"
                >{{ t("overview.graph") }}</MaterialCheckbox
              ><MaterialCheckbox
                id="overview-data"
                v-model="showData"
                :checked="showData"
                name="overview-data"
                >{{ t("overview.data") }}</MaterialCheckbox
              >
            </header>
            <div v-if="showGraph" class="legacy-graphs">
              <article
                v-for="graph in model.donuts"
                :key="graph.key"
                class="legacy-graph"
              >
                <h3>{{ t("overview." + graph.key) }}</h3>
                <p class="donut-total">
                  {{ money(graph.total) }} {{ t("overview.baht") }}
                </p>
                <svg
                  v-if="graph.total > 0"
                  viewBox="0 0 260 240"
                  role="img"
                  :aria-label="donutLabel(graph)"
                >
                  <circle
                    cx="130"
                    cy="120"
                    r="78"
                    fill="none"
                    stroke="#f0f2f5"
                    stroke-width="44"
                  />
                  <g
                    v-for="segment in donutSegments(graph.rows)"
                    :key="segment.key"
                  >
                    <circle
                      cx="130"
                      cy="120"
                      r="78"
                      fill="none"
                      :stroke="segment.color"
                      stroke-width="44"
                      pathLength="100"
                      :stroke-dasharray="`${Math.max(0, segment.percent - 0.7)} ${100 - Math.max(0, segment.percent - 0.7)}`"
                      :stroke-dashoffset="-segment.start"
                      transform="rotate(-90 130 120)"
                    />
                    <text
                      v-if="segment.percent >= 3"
                      :x="segment.x"
                      :y="segment.y + 1"
                      text-anchor="middle"
                      dominant-baseline="middle"
                      :fill="
                        ['#10b981', '#f59e0b'].includes(segment.color)
                          ? '#153b32'
                          : '#fff'
                      "
                    >
                      {{ pct(segment.value, graph.total) }}
                    </text>
                  </g>
                </svg>
                <p v-else class="empty donut-empty">{{ t("overview.zero") }}</p>
                <div class="donut-legend">
                  <div v-for="row in graph.rows" :key="row.key">
                    <span
                      ><i class="dot" :style="{ background: row.color }" />{{
                        rowLabel(row)
                      }}</span
                    ><strong>{{ money(row.value) }}</strong
                    ><small>{{ pct(row.value, graph.total) }}</small>
                  </div>
                </div>
              </article>
            </div>
            <div v-if="showData" class="legacy-data">
              <div class="legacy-summary">
                <span
                  >{{ t("overview.newTickets") }} :
                  <b class="red">{{ fmt(model.incomingCount) }}</b></span
                ><span
                  >{{ t("overview.incoming") }} :
                  <b class="red">{{ money(model.incoming) }}</b></span
                ><span
                  >{{ t("overview.closedTickets") }} :
                  <b class="green">{{ fmt(model.outgoingCount) }}</b></span
                ><span
                  >{{ t("overview.outgoing") }} :
                  <b class="green">{{ money(model.outgoing) }}</b></span
                >
              </div>
              <div class="legacy-columns">
                <div>
                  <h3>
                    <button
                      class="link"
                      :disabled="!model.count"
                      @click="emit('history', 1)"
                    >
                      {{ t("overview.normalSummary") }}
                    </button>
                    : <b class="blue">{{ fmt(model.count) }}</b>
                  </h3>
                  <div
                    v-for="row in model.rows"
                    :key="row.key"
                    class="legacy-row"
                  >
                    <button
                      class="link"
                      :disabled="!row.count"
                      @click="emit('history', row.history)"
                    >
                      {{ rowLabel(row) }}
                      <span class="blue">({{ fmt(row.count) }})</span></button
                    ><span
                      >{{ t("overview.ticketValue") }}
                      {{ money(row.value) }}</span
                    ><span v-if="row.key !== 'pawn'"
                      >{{ t("overview.interestColumn") }}
                      {{ money(row.interest) }}</span
                    ><span v-if="['increase', 'reduce'].includes(row.key)"
                      >{{ t("overview.diff") }} {{ money(row.diff) }}</span
                    >
                  </div>
                  <div class="legacy-totals">
                    <span
                      ><button
                        class="link"
                        :disabled="!model.pawnIncrease"
                        @click="emit('history', 11)"
                      >
                        {{ t("overview.pawnIncrease") }}
                      </button>
                      : <b class="red">{{ money(model.pawnIncrease) }}</b></span
                    ><span
                      ><button
                        class="link"
                        :disabled="!model.others"
                        @click="emit('history', 12)"
                      >
                        {{ t("overview.otherTypes") }}
                      </button>
                      : <b class="green">{{ money(model.others) }}</b></span
                    ><span
                      >{{ t("overview.allTickets") }} :
                      <b class="blue">{{ money(model.value) }}</b></span
                    ><span
                      ><button
                        class="link"
                        :disabled="!model.interest"
                        @click="emit('report', 'profit')"
                      >
                        {{ t("overview.allInterest") }}
                      </button>
                      : <b class="green">{{ money(model.interest) }}</b></span
                    >
                  </div>
                </div>
                <div>
                  <h3>
                    <button
                      class="link"
                      :disabled="!model.migrationCount"
                      @click="emit('history', 2)"
                    >
                      {{ t("overview.migrationSummary") }}
                    </button>
                    : <b class="blue">{{ fmt(model.migrationCount) }}</b>
                  </h3>
                  <div
                    v-for="row in model.migrations"
                    :key="row.key"
                    class="legacy-row migration-row"
                  >
                    <button
                      class="link"
                      :disabled="!row.count"
                      @click="emit('history', row.history)"
                    >
                      {{ rowLabel(row) }} ({{ t("overview.migration") }})
                      <span class="blue">({{ fmt(row.count) }})</span></button
                    ><span
                      >{{ t("overview.ticketValue") }}
                      {{ money(row.value) }}</span
                    >
                  </div>
                  <p class="legacy-migration-total">
                    {{ t("overview.allTickets") }} :
                    <b class="red">{{ money(model.migrationValue) }}</b>
                  </p>
                </div>
              </div>
            </div>
          </section>
          <p class="line-note">{{ t("overview.lineNote") }}</p>
        </template>
      </section>
    </template>
    <dialog ref="infoDialog" class="overview-dialog" @click="closeOnBackdrop">
      <header>
        <div>
          <h2>{{ detailTitle }}</h2>
          <small>{{ periodLabel }}</small>
        </div>
        <button :aria-label="t('overview.close')" @click="infoDialog.close()">
          <i class="material-icons" aria-hidden="true">close</i>
        </button>
      </header>
      <p>{{ detailDescription }}</p>
      <div class="dialog-metrics">
        <div
          v-for="metric in detailMetrics"
          :key="metric.key"
          :class="detailMetricTone(metric)"
        >
          <span>{{ t("overview." + metric.key) }}</span>
          <strong>{{ formatDetailMetric(metric) }}</strong>
        </div>
      </div>
      <div v-if="detailRows.length" class="dialog-breakdown">
        <h3>{{ t("overview.components") }}</h3>
        <div
          class="dialog-breakdown-head"
          :class="{ 'has-interest': showDetailInterestColumn }"
        >
          <span>{{ t("overview.type") }}</span>
          <span>{{ t("overview.count") }}</span>
          <span>{{ t("overview.ticketValue") }}</span>
          <span v-if="showDetailInterestColumn">{{ t("overview.interestColumn") }}</span>
        </div>
        <button
          v-for="row in detailRows"
          :key="row.key"
          type="button"
          :class="{ 'has-interest': showDetailInterestColumn }"
          @click="openTransactionDetail(row)"
        >
          <span class="dialog-row-name">
            <i class="dot" :style="{ background: row.color }" />
            {{ rowLabel(row) }}
            <small v-if="row.migration">{{ t("overview.migration") }}</small>
          </span>
          <span>{{ fmt(row.count) }} {{ t("overview.items") }}</span>
          <strong>{{ money(row.displayValue ?? row.value) }} {{ t("overview.baht") }}</strong>
          <strong v-if="showDetailInterestColumn">
            {{ money(row.interest) }} {{ t("overview.baht") }}
          </strong>
        </button>
      </div>
    </dialog>
  </section>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import MaterialButton from "@/components/MaterialButton.vue";
import MaterialCheckbox from "@/components/MaterialCheckbox.vue";
import messages from "./overviewMessages";
import {
  buildOverview,
  buildCardDetail,
  number,
  day,
  presetRange,
  validateRange,
  donutSegments,
} from "./overviewModel.mjs";
const props = defineProps({
  data: { type: Object, default: () => ({}) },
  lineCount: { type: Number, default: 0 },
  lineLoading: Boolean,
  lineFailed: Boolean,
  busy: Boolean,
  ready: Boolean,
  error: Boolean,
  selected: Boolean,
  updatedAt: { type: String, default: "" },
  from: { type: Date, required: true },
  to: { type: Date, required: true },
});
const emit = defineEmits(["search", "retry", "report", "history", "otp"]);
const { t, locale } = useI18n({ useScope: "local", messages });
const n = (key) => number(props.data[key]);
const model = computed(() => buildOverview(props.data));
const fmt = (value) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(
    number(value),
  );
const money = (value) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number(value));
const signed = (value) => (value > 0 ? "+" : "") + fmt(value);
const valueTone = (value) =>
  number(value) > 0
    ? "value-positive"
    : number(value) < 0
      ? "value-negative"
      : "value-neutral";
const pct = (value, total) =>
  (total ? (value / total) * 100 : 0).toFixed(1).replace(/\.0$/, "") + "%";
const rowLabel = (row) => t("overview." + (row.labelKey || row.key));
const showMigration = ref(false),
  showGraph = ref(false),
  showData = ref(false),
  showTransactionDetails = ref(false),
  chartMode = ref("count");
const draftFrom = ref(day(props.from)),
  draftTo = ref(day(props.to)),
  activePeriod = ref("today"),
  dateError = ref("");
const today = computed(() => day());
const minDate = computed(() => {
  const value = day();
  value.setMonth(value.getMonth() - 3);
  return value;
});
watch(
  () => [props.from, props.to],
  ([from, to]) => {
    draftFrom.value = day(from);
    draftTo.value = day(to);
  },
);
watch([draftFrom, draftTo], () => {
  dateError.value = "";
  activePeriod.value =
    ["today", "week", "month"].find((key) => {
      const range = presetRange(key);
      return (
        +day(draftFrom.value) === +range.from &&
        +day(draftTo.value) === +range.to
      );
    }) || "";
});
function applyRange() {
  dateError.value = validateRange(draftFrom.value, draftTo.value);
  if (dateError.value) return;
  emit("search", { from: day(draftFrom.value), to: day(draftTo.value) });
}
function choosePeriod(key) {
  const range = presetRange(key);
  draftFrom.value = range.from;
  draftTo.value = range.to;
  activePeriod.value = key;
  applyRange();
}
const periodLabel = computed(() => {
  const format = (d) =>
    new Intl.DateTimeFormat(locale.value === "th" ? "th-TH" : "en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(d);
  return format(props.from) + " – " + format(props.to);
});
const snapshot = computed(() => [
  {
    key: "active",
    value: n("numOfActiveWorkCase"),
    icon: "confirmation_number",
  },
  { key: "defer", value: n("numOfPostponeWorkCase"), icon: "schedule" },
  { key: "represent", value: n("numOfRepresentWorkCase"), icon: "fingerprint" },
  { key: "outage", value: n("numOfOutageWorkCase"), icon: "bolt" },
  { key: "line", value: props.lineCount, icon: "chat_bubble_outline" },
]);
const usage = computed(() => [
  { key: "customers", value: n("numOfCustomer") },
  { key: "cancel", value: n("numOfCancelTicket") },
  { key: "terminate", value: n("numOfTerminate") },
  { key: "edit", value: n("numOfEditAfterComplete") },
]);
const cards = computed(() => [
  { key: "incoming", value: model.value.incoming },
  { key: "outgoing", value: model.value.outgoing },
  { key: "interest", value: model.value.interest },
  { key: "ticketNet", value: model.value.ticketNet },
]);
const cashEntries = computed(() => [
  { key: "receive", value: model.value.receive },
  { key: "pay", value: model.value.pay },
  { key: "cashNet", value: model.value.cashNet },
]);
const cashMax = computed(() =>
  Math.max(model.value.receive, model.value.pay, 1),
);
const cashDescription = computed(() =>
  cashEntries.value
    .map(
      (e) =>
        t("overview." + e.key) +
        " " +
        money(e.value) +
        " " +
        t("overview.baht"),
    )
    .join(", "),
);
const chartTotal = computed(() =>
  model.value.rows.reduce((sum, row) => sum + row[chartMode.value], 0),
);
const chartMax = computed(() =>
  Math.max(...model.value.rows.map((row) => row[chartMode.value]), 1),
);
const visibleRows = computed(() =>
  showMigration.value
    ? [...model.value.rows, ...model.value.migrations]
    : model.value.rows,
);
const donutLabel = (graph) =>
  t("overview." + graph.key) +
  ": " +
  graph.rows
    .map((row) => rowLabel(row) + " " + pct(row.value, graph.total))
    .join(", ");
const infoDialog = ref(null);
const detailState = ref({ kind: "card", key: "incoming", row: null, rows: [] });
const detailTitle = computed(() => {
  if (detailState.value.kind === "transaction") {
    const row = detailState.value.row;
    return (
      rowLabel(row) +
      (row.migration ? " · " + t("overview.migration") : "")
    );
  }
  return t("overview." + detailState.value.key);
});
const detailDescription = computed(() => {
  if (detailState.value.kind === "card") {
    return t("overview." + detailState.value.key + "Info");
  }
  if (detailState.value.kind === "transaction") {
    return detailState.value.row.migration
      ? t("overview.migrationDetailInfo")
      : t("overview.transactionDetailInfo");
  }
  return t("overview.detailsHint");
});
const detailMetrics = computed(() => {
  if (detailState.value.kind === "card") {
    return buildCardDetail(model.value, detailState.value.key).metrics;
  }
  if (detailState.value.kind === "transaction") {
    const row = detailState.value.row;
    return [
      { key: "count", value: row.count, format: "count" },
      { key: "ticketValue", value: row.value, format: "money" },
      ...(row.interest === null
        ? []
        : [{ key: "interestColumn", value: row.interest, format: "money" }]),
      ...(row.diff
        ? [{ key: "diff", value: row.diff, format: "money" }]
        : []),
    ];
  }
  const rows = detailState.value.rows;
  return [
    {
      key: "count",
      value: rows.reduce((sum, row) => sum + row.count, 0),
      format: "count",
    },
    {
      key: "ticketValue",
      value: rows.reduce((sum, row) => sum + row.value, 0),
      format: "money",
    },
    ...(rows.some((row) => row.interest !== null)
      ? [
          {
            key: "interestColumn",
            value: rows.reduce((sum, row) => sum + number(row.interest), 0),
            format: "money",
          },
        ]
      : []),
  ];
});
const detailRows = computed(() => {
  if (detailState.value.kind === "card") {
    return buildCardDetail(model.value, detailState.value.key).rows;
  }
  return [];
});
const showDetailInterestColumn = computed(
  () => detailState.value.kind === "card" && detailState.value.key === "interest",
);
function showDetail() {
  if (infoDialog.value.open) infoDialog.value.close();
  infoDialog.value.showModal();
}
function openCardDetail(key) {
  detailState.value = { kind: "card", key, row: null, rows: [] };
  showDetail();
}
function openTransactionDetail(row) {
  detailState.value = { kind: "transaction", key: row.key, row, rows: [] };
  showDetail();
}
function formatDetailMetric(metric) {
  if (metric.format === "count") {
    return fmt(metric.value) + " " + t("overview.items");
  }
  if (metric.format === "signedCount") {
    return signed(metric.value) + " " + t("overview.tickets");
  }
  return money(metric.value) + " " + t("overview.baht");
}
function detailMetricTone(metric) {
  return ["ticketNet", "stockNet"].includes(metric.key)
    ? valueTone(metric.value)
    : "";
}
function closeOnBackdrop(event) {
  if (event.target !== infoDialog.value) return;
  const r = infoDialog.value.getBoundingClientRect();
  if (
    event.clientX < r.left ||
    event.clientX > r.right ||
    event.clientY < r.top ||
    event.clientY > r.bottom
  )
    infoDialog.value.close();
}
</script>

<style scoped>
.overview {
  --brand: var(--bs-primary, #05af50);
  --forest: var(--bs-secondary, #05602d);
  --ink: #344767;
  --muted: #67748e;
  --line: #e1e8e4;
  --mint: #e8fef2;
  padding: 24px;
  color: var(--ink);
  font-family: var(
    --bs-body-font-family,
    "THSarabunNew",
    "Sukhumvit Set",
    "Prompt",
    "Tahoma",
    "Roboto",
    sans-serif
  );
  font-size: 15px;
  line-height: 1.6;
  border-radius: 12px;
  min-width: 0;
}
.overview h1,
.overview h2,
.overview h3,
.overview p {
  margin: 0;
  color: inherit;
}
.overview h1,
.overview h2,
.overview h3 {
  font-family: inherit;
}
.overview h1 {
  font-size: 26px;
  font-weight: 700;
}
.overview h2 {
  font-size: 20px;
  font-weight: 600;
}
.overview h3 {
  font-size: 16px;
  font-weight: 500;
}
.overview p,
.overview small {
  color: var(--muted);
}
.overview p {
  font-size: 14px;
  margin-top: 4px;
}
.overview button {
  font-family: inherit;
}
.overview button:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 3px;
}
.overview button:disabled {
  cursor: default;
}
.overview strong,
.overview b {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.overview small {
  font-size: 12px;
}
.overview-heading {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 24px;
}
.updated {
  font-size: 13px;
  color: var(--muted);
  margin-top: 10px;
}
.updated strong {
  color: #dc2626;
}
.scope {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  grid-template-rows: auto auto;
  gap: 1px 10px;
  align-content: start;
  align-items: center;
  max-width: 410px;
}
.scope-label {
  grid-column: 2;
  font-size: 13px;
  color: var(--muted);
}
.scope-icon {
  grid-column: 1;
  grid-row: 2;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  background: var(--mint);
  color: var(--forest);
  border-radius: 10px;
  font-size: 22px;
}
.scope-control {
  grid-column: 2;
  grid-row: 2;
  min-width: 0;
}
.scope-control :deep(.shop-scope > span) {
  display: none;
}
.scope-control :deep(.shop-scope-select) {
  width: 100%;
  min-width: 200px;
  max-width: 330px;
  font-size: 14px;
  height: 42px;
  margin-bottom: 0 !important;
}
.scope-control :deep(.shop-scope) {
  min-width: 0;
}
.scope-control :deep(.shop-scope-manage) {
  flex-shrink: 0;
}
.status {
  padding: 28px;
  border-radius: 12px;
  background: #f8f9fa;
  text-align: center;
  margin: 18px 0;
}
.status.error {
  color: #b42335;
  background: #fff2f4;
}
.status .btn {
  margin-left: 12px;
}
.snapshot-section > h2 {
  margin-bottom: 14px;
}
.snapshot {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  padding: 20px 0;
  border: 1px solid var(--line);
  border-radius: 16px;
  margin-bottom: 28px;
}
.snapshot-item {
  padding: 0 17px;
  border-right: 1px solid var(--line);
  min-width: 0;
}
.snapshot-item:last-child {
  border: 0;
}
.snapshot-label {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  color: var(--muted);
  font-size: 14px;
  min-height: 44px;
}
.snapshot-label i {
  font-size: 17px;
  color: var(--forest);
  margin-top: 3px;
}
.snapshot-label-link {
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}
.snapshot-label-link:hover {
  color: var(--brand);
}
.snapshot-label-link:focus-visible {
  border-radius: 3px;
  outline: 3px solid rgba(16, 185, 129, 0.22);
  outline-offset: 3px;
}
.snapshot-value {
  font-size: 30px;
  font-weight: 600;
  margin-top: 8px;
}
.snapshot-value small {
  font-size: 13px;
  font-weight: 400;
}
.line-value {
  color: var(--brand);
}
.link {
  background: none;
  border: 0;
  color: inherit;
  padding: 0;
  text-align: left;
  text-decoration: underline;
}
.link:disabled {
  text-decoration: none;
}
.period-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 18px;
}
.period-controls {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-left: auto;
}
.segments {
  display: flex;
  background: #f0f2f5;
  padding: 3px;
  border-radius: 7px;
  gap: 2px;
  flex-shrink: 0;
  min-height: 42px;
}
.segments button {
  border: 0;
  background: transparent;
  color: var(--muted);
  border-radius: 5px;
  padding: 6px 10px;
  font-size: 14px;
  white-space: nowrap;
}
.segments button[aria-pressed="true"] {
  background: var(--brand);
  color: #fff;
}
.date-range {
  display: grid;
  grid-template-columns: 150px 150px auto;
  gap: 10px;
  align-items: end;
}
.date-field {
  min-width: 0;
}
.date-field label {
  display: block;
  font-size: 13px;
  color: var(--muted);
  margin: 0 0 5px;
}
.date-field :deep(.dp__input) {
  font-family: inherit;
  font-size: 14px;
  min-height: 42px;
  border-radius: 8px;
  color: var(--ink);
  border-color: #dce4df;
}
.date-range .btn {
  height: 42px;
  margin: 0;
  padding: 8px 16px;
  font-size: 14px;
  box-shadow: none;
  text-transform: none;
  border-radius: 8px;
  white-space: nowrap;
}
.date-error {
  grid-column: 1/-1;
  color: #c62828 !important;
}
.kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 22px;
}
.kpi {
  padding: 22px 20px 17px;
  border: 1px solid #dce8f7;
  border-radius: 18px;
  background: linear-gradient(145deg, #eff6ff, #fff 76%);
  min-width: 0;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease,
    border-color 0.18s ease;
}
.kpi:hover {
  transform: translateY(-2px);
  border-color: var(--brand);
  box-shadow: 0 10px 28px rgba(52, 71, 103, 0.1);
}
.kpi:focus-visible {
  outline: 3px solid rgba(5, 175, 80, 0.22);
  outline-offset: 3px;
}
.kpi.outgoing {
  background: linear-gradient(145deg, #fff1f3, #fff 76%);
  border-color: #f3dfe5;
}
.kpi.interest {
  background: linear-gradient(145deg, #fff5df, #fffdf8 85%);
  border-color: #eddfc3;
}
.kpi.ticketNet {
  background: linear-gradient(145deg, #edf9f5, #fff 76%);
  border-color: #d7eae3;
}
.kpi-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}
.kpi-head h3 {
  font-size: 16px;
}
.info-button {
  display: flex;
  border: 0;
  background: none;
  padding: 0;
  color: var(--muted);
}
.info-button i {
  font-size: 17px;
}
.kpi-value {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 20px;
}
.kpi-value strong {
  font-size: 38px;
  line-height: 1.2;
  letter-spacing: -0.6px;
  overflow-wrap: anywhere;
}
.kpi-value span {
  font-size: 13px;
  color: var(--muted);
}
.interest .kpi-value {
  color: #79591f;
}
.ticketNet .kpi-value {
  color: #96703b;
}
.ticketNet .value-positive,
.ticketNet .kpi-footer .value-positive,
.dialog-metrics .value-positive strong {
  color: #087c56;
}
.ticketNet .value-negative,
.ticketNet .kpi-footer .value-negative,
.dialog-metrics .value-negative strong {
  color: #dc2626;
}
.kpi-exact {
  font-size: 13px;
  color: var(--muted);
  margin: 8px 0 20px;
  min-height: 42px;
}
.kpi-footer {
  margin-top: auto;
  border-top: 1px solid var(--line);
  padding-top: 13px;
  font-size: 13px;
  color: var(--muted);
}
.panel {
  border: 1px solid var(--line);
  border-radius: 18px;
  padding: 24px;
  min-width: 0;
  background: white;
}
.panel-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}
.cashflow {
  margin-bottom: 22px;
  background: linear-gradient(120deg, #f8fcfa, #fff 55%);
}
.cashflow header h2 {
  font-size: 22px;
}
.cash-totals {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 24px 0;
  gap: 20px;
}
.cash-totals > div {
  border-right: 1px solid var(--line);
  padding-right: 16px;
  min-width: 0;
}
.cash-totals > div:last-child {
  border: 0;
}
.cash-totals > div > span {
  font-size: 15px;
  color: var(--muted);
}
.cash-totals strong {
  font-size: 30px;
  overflow-wrap: anywhere;
}
.cash-totals .receive {
  color: #087c56;
}
.cash-totals .pay {
  color: #dc2626;
}
.cash-chart {
  padding: 20px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
}
.chart-caption {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: var(--muted);
  gap: 12px;
}
.cash-row {
  display: grid;
  grid-template-columns: 85px minmax(0, 1fr) 155px;
  gap: 14px;
  align-items: center;
  margin-top: 18px;
  font-size: 14px;
}
.cash-row strong {
  text-align: right;
  font-size: 15px;
}
.cash-track {
  height: 22px;
  border-radius: 6px;
  background: #f0f4f2;
  overflow: hidden;
}
.cash-track > div {
  height: 100%;
  border-radius: 6px;
}
.cash-track .receive {
  background: #10b981;
}
.cash-track .pay {
  background: #ef4444;
}
.cash-note {
  font-size: 13px !important;
  margin-top: 14px !important;
}
.charts {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 20px;
  margin-bottom: 22px;
}
.chart-total {
  font-size: 32px;
  font-weight: 600;
  margin: 18px 0;
}
.chart-total small {
  font-weight: 400;
  font-size: 14px;
}
.transaction-bars {
  display: grid;
  gap: 16px;
}
.transaction-row {
  display: grid;
  grid-template-columns: 62px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  width: 100%;
  padding: 0;
  background: none;
  border: 0;
  text-align: left;
  color: var(--ink);
  font-size: 15px;
}
.bar-track {
  height: 20px;
  background: #f0f2f5;
  border-radius: 5px;
  overflow: hidden;
}
.bar-track > span {
  display: block;
  height: 100%;
  border-radius: 5px;
}
.bar-value {
  text-align: right;
  min-width: 100px;
  font-variant-numeric: tabular-nums;
}
.bar-value small {
  margin-left: 6px;
}
.panel-note {
  font-size: 13px !important;
  margin-top: 20px !important;
}
.interest-stack {
  display: flex;
  gap: 3px;
  height: 14px;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 22px;
}
.interest-list {
  display: grid;
  gap: 14px;
}
.interest-list > div {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 45px;
  gap: 10px;
  align-items: center;
  font-size: 15px;
}
.interest-list strong {
  font-size: 16px;
}
.interest-list small {
  text-align: right;
}
.dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
}
.empty {
  padding: 24px 10px;
  text-align: center;
  color: var(--muted);
  background: #f8faf9;
  border-radius: 10px;
}
.health {
  display: grid;
  grid-template-columns: 1.3fr repeat(5, minmax(0, 1fr));
  gap: 16px;
  padding: 22px;
  border: 1px solid var(--line);
  border-radius: 16px;
  margin-bottom: 22px;
}
.health h2 {
  font-size: 17px;
}
.health > div + div {
  border-left: 1px solid var(--line);
  padding-left: 15px;
}
.health span {
  display: block;
  font-size: 13px;
  color: var(--muted);
}
.health strong,
.otp-values {
  display: block;
  font-size: 25px;
  margin-top: 7px;
}
.otp-values button {
  border: 0;
  padding: 0;
  background: none;
  color: var(--ink);
  font-weight: 600;
}
.otp-values button:not(:disabled) {
  text-decoration: underline;
}
.health .otp-values {
  white-space: nowrap;
}
.transaction-table {
  padding: 0;
  margin-bottom: 22px;
  overflow: hidden;
}
.transaction-table header {
  padding: 24px;
  align-items: center;
  gap: 18px;
}
.transaction-details-toggle {
  display: flex;
  flex: 1;
  min-width: 240px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--ink);
  text-align: left;
  cursor: pointer;
}
.transaction-details-toggle > span {
  display: grid;
  gap: 2px;
}
.transaction-details-toggle strong {
  font-size: 20px;
  font-weight: 600;
}
.transaction-details-toggle small {
  color: var(--muted);
  font-size: 14px;
}
.transaction-details-toggle i {
  display: grid;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  place-items: center;
  border-radius: 9px;
  background: var(--mint);
  color: var(--brand);
}
.transaction-details-toggle:focus-visible {
  outline: 3px solid rgba(16, 185, 129, 0.22);
  outline-offset: 6px;
  border-radius: 8px;
}
.overview :deep(.form-check) {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
}
.overview :deep(.form-check-input) {
  margin: 0;
  float: none;
  flex-shrink: 0;
  position: relative;
}
.overview :deep(.custom-control-label) {
  margin: 0;
  color: var(--muted);
  font-size: 14px;
}
.overview table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
  white-space: nowrap;
}
.overview th,
.overview td {
  padding: 14px 18px;
  text-align: right;
  border-bottom: 1px solid var(--line);
}
.overview th:first-child,
.overview td:first-child {
  text-align: left;
}
.overview thead {
  background: #194d30;
  color: #fff;
}
.overview tfoot {
  background: #90f9be;
  color: var(--ink);
  font-weight: 600;
}
.overview tbody button {
  background: none;
  border: 0;
  padding: 0;
  color: inherit;
  text-align: left;
}
.overview tbody tr:hover {
  background: #f3fbf7;
}
.transaction-table > .panel-note {
  padding: 0 24px 18px;
}
.legacy-heading {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}
.legacy-graphs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  margin-top: 26px;
}
.legacy-graph {
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 20px 16px;
  text-align: center;
  min-width: 0;
}
.legacy-graph h3 {
  font-size: 17px;
}
.donut-total {
  font-size: 18px !important;
  margin: 10px 0 !important;
}
.legacy-graph svg {
  display: block;
  width: 100%;
  max-width: 260px;
  margin: auto;
}
.legacy-graph svg text {
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
}
.donut-empty {
  min-height: 180px;
  display: grid;
  place-items: center;
}
.donut-legend {
  display: grid;
  gap: 10px;
  font-size: 13px;
  margin-top: 12px;
}
.donut-legend > div {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 38px;
  gap: 6px;
  align-items: center;
  text-align: right;
}
.donut-legend > div > span {
  text-align: left;
}
.donut-legend strong {
  font-weight: 400;
}
.legacy-data {
  margin-top: 26px;
}
.legacy-summary {
  display: flex;
  gap: 20px 32px;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 34px;
  font-size: 17px;
}
.legacy-columns {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr);
  gap: 30px;
}
.legacy-columns h3 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 25px;
}
.legacy-row {
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  margin-bottom: 22px;
  font-size: 15px;
  align-items: start;
}
.legacy-row > span:last-child:nth-child(4) {
  grid-column: 2/-1;
}
.migration-row {
  grid-template-columns: minmax(0, 1fr);
  gap: 5px;
}
.legacy-totals {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  font-size: 16px;
  margin-top: 30px;
}
.legacy-migration-total {
  margin-top: 30px !important;
  font-size: 16px !important;
}
.red {
  color: #dc2626;
}
.green {
  color: #087c56;
}
.blue {
  color: #1a73e8;
}
.line-note {
  font-size: 12px !important;
  margin-top: 16px !important;
}
.overview-dialog {
  width: min(680px, calc(100vw - 32px));
  border: 1px solid var(--line);
  padding: 24px;
  border-radius: 16px;
  color: var(--ink);
  box-shadow: 0 16px 50px #142c3326;
}
.overview-dialog::backdrop {
  background: #142c3370;
}
.overview-dialog header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}
.overview-dialog header small {
  display: block;
  margin-top: 3px;
  color: var(--muted);
  font-size: 13px;
}
.overview-dialog header button {
  border: 0;
  background: #f0f2f5;
  border-radius: 6px;
  padding: 4px;
  display: flex;
  color: var(--ink);
}
.overview-dialog p {
  font-size: 16px;
}
.dialog-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 20px;
}
.dialog-metrics > div {
  padding: 13px 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #f8faf9;
}
.dialog-metrics span,
.dialog-metrics strong {
  display: block;
}
.dialog-metrics span {
  color: var(--muted);
  font-size: 13px;
}
.dialog-metrics strong {
  margin-top: 4px;
  color: var(--ink);
  font-size: 17px;
}
.dialog-breakdown {
  margin-top: 22px;
  overflow-x: auto;
}
.dialog-breakdown h3 {
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 600;
}
.dialog-breakdown-head,
.dialog-breakdown > button {
  display: grid;
  grid-template-columns: minmax(170px, 1fr) 100px 170px;
  gap: 12px;
  align-items: center;
  width: 100%;
  min-width: 520px;
  padding: 11px 4px;
  border: 0;
  border-bottom: 1px solid var(--line);
  background: transparent;
  color: var(--ink);
  text-align: right;
}
.dialog-breakdown-head {
  padding: 7px 4px;
  border-bottom: 1px solid var(--line);
  color: var(--muted);
  font-size: 12px;
}
.dialog-breakdown-head > span:first-child {
  text-align: left;
}
.dialog-breakdown-head > span:not(:first-child),
.dialog-breakdown > button > span:not(:first-child),
.dialog-breakdown > button > strong {
  text-align: right;
  white-space: nowrap;
}
.dialog-breakdown-head.has-interest,
.dialog-breakdown > button.has-interest {
  grid-template-columns: minmax(150px, 1fr) 90px 155px 155px;
  min-width: 600px;
}
.dialog-breakdown > button:hover {
  background: var(--mint);
}
.dialog-row-name {
  display: flex;
  align-items: center;
  gap: 7px;
  text-align: left;
}
.dialog-row-name small {
  padding: 1px 5px;
  border-radius: 4px;
  background: #f0edf6;
  color: #7b678e;
  font-size: 11px;
}
@media (max-width: 575px) {
  .dialog-metrics {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 1399px) {
  .kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .charts {
    grid-template-columns: 1fr;
  }
  .snapshot-item {
    padding: 0 12px;
  }
  .snapshot-label {
    font-size: 13px;
  }
  .snapshot-value {
    font-size: 27px;
  }
  .health {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .health > div:nth-child(4) {
    border: 0;
    padding-left: 0;
  }
  .legacy-graphs {
    gap: 12px;
  }
  .legacy-graph {
    padding: 18px 12px;
  }
  .legacy-columns {
    grid-template-columns: 1fr;
  }
  .migration-row {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
}
@media (max-width: 1000px) {
  .overview-heading {
    grid-template-columns: 1fr;
  }
  .scope {
    justify-self: end;
  }
  .cash-totals strong {
    font-size: 25px;
  }
  .snapshot {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    row-gap: 20px;
  }
  .snapshot-item:nth-child(3) {
    border: 0;
  }
  .legacy-graphs {
    grid-template-columns: 1fr;
  }
  .legacy-graph {
    padding: 20px;
  }
  .donut-legend {
    max-width: 400px;
    margin: 12px auto 0;
  }
  .period-controls {
    margin-left: 0;
  }
}
@media (max-width: 720px) {
  .overview {
    padding: 18px 14px;
  }
  .overview-heading {
    gap: 18px;
  }
  .overview h1 {
    font-size: 24px;
  }
  .scope {
    justify-self: stretch;
    max-width: none;
    grid-template-columns: 32px minmax(0, 1fr);
  }
  .scope-icon {
    width: 32px;
    height: 32px;
    font-size: 20px;
  }
  .scope-control :deep(.shop-scope-select) {
    max-width: none;
    min-width: 0;
  }
  .snapshot {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .snapshot-item:nth-child(3) {
    border-right: 1px solid var(--line);
  }
  .snapshot-item:nth-child(even) {
    border: 0;
  }
  .snapshot-item:last-child {
    grid-column: 1/-1;
    border-top: 1px solid var(--line);
    padding-top: 16px;
  }
  .snapshot-item:last-child .snapshot-label {
    min-height: 0;
  }
  .period-controls {
    flex-wrap: wrap;
    width: 100%;
  }
  .date-range {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
    gap: 12px;
  }
  .date-range .btn {
    grid-column: 1/-1;
  }
  .period-controls > .segments {
    width: 100%;
  }
  .period-controls > .segments button {
    flex: 1;
  }
  .kpi {
    padding: 20px;
  }
  .kpi-value strong {
    font-size: 34px;
  }
  .panel {
    padding: 20px 14px;
  }
  .cash-totals {
    grid-template-columns: 1fr;
    gap: 18px;
  }
  .cash-totals > div {
    border: 0;
    border-bottom: 1px solid var(--line);
    padding: 0 0 16px;
  }
  .cash-totals strong {
    font-size: 30px;
  }
  .cash-chart {
    padding: 16px 12px;
  }
  .cash-row {
    grid-template-columns: 1fr auto;
    gap: 8px;
  }
  .cash-row strong {
    grid-row: 1;
    grid-column: 2;
  }
  .cash-track {
    grid-row: 2;
    grid-column: 1/-1;
  }
  .chart-caption {
    flex-wrap: wrap;
  }
  .transaction-row {
    grid-template-columns: 56px minmax(0, 1fr) auto;
    font-size: 14px;
  }
  .bar-value {
    min-width: 75px;
  }
  .bar-value small {
    display: block;
  }
  .health {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 20px 16px;
  }
  .health > div:first-child {
    grid-column: 1/-1;
  }
  .health > div + div {
    padding: 0;
    border: 0;
  }
  .transaction-table {
    padding: 0;
  }
  .transaction-table header {
    padding: 20px 14px;
  }
  .legacy-heading {
    gap: 16px;
  }
  .legacy-heading h2 {
    width: 100%;
  }
  .legacy-summary {
    font-size: 16px;
    gap: 14px;
  }
  .legacy-row {
    grid-template-columns: 1fr 1fr;
    font-size: 14px;
  }
  .legacy-row > button {
    grid-column: 1/-1;
  }
  .legacy-row > span:last-child:nth-child(4) {
    grid-column: 1/-1;
  }
  .legacy-totals {
    grid-template-columns: 1fr;
    font-size: 15px;
  }
  .legacy-columns h3 {
    font-size: 17px;
  }
}
@media (max-width: 540px) {
  .kpis {
    grid-template-columns: 1fr;
  }
  .kpi-value strong {
    font-size: 40px;
  }
  .kpi-head h3 {
    font-size: 17px;
  }
  .kpi-exact,
  .kpi-footer {
    font-size: 14px;
  }
  .interest-list > div {
    font-size: 14px;
    gap: 7px;
  }
}
</style>
